import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Eraser, Hand, Pencil, RotateCcw, Square, X } from "lucide-react";

/**
 * Çizim katmanı — materyalin (PDF, pekiştirme testi, video) ÜSTÜNE yazmak için.
 *
 * Zemin varsayılan olarak ŞEFFAFTIR: öğrenci PDF'in veya sorunun üzerine işlem yapar,
 * altındaki materyal görünmeye devam eder. Boş sayfa isteyen "Beyaz zemin"e geçer.
 *
 * "İçeriğe dokun" modu şart: çizim katmanı tüm alanı kapladığı için açıkken PDF
 * kaydırılamaz, test şıkkı işaretlenemez. O modda katman `pointer-events: none`
 * olur — çizim ekranda kalır ama tıklamalar altındaki materyale geçer.
 *
 * Pointer Events kullanır: fare, dokunmatik ve KALEM (Apple Pencil/S Pen) tek yoldan
 * gider. Çizim tek pointerId'ye kilitlenir ki kalemle yazarken avuç teması iz bırakmasın.
 *
 * NOT: katman materyalle birlikte KAYMAZ. PDF kaydırılırsa çizim yerinde kalır —
 * ekrana not almak için uygundur, sayfaya kalıcı işaretleme için değil.
 *
 * Soru soru ekranlar için: `sheets` + `sheetKey` her sorunun çizimini ayrı tutar, canvas kabının
 * boyunu izler (soru görseli geç yüklenir). `toolbarHost` araç çubuğunu dışarı alır, soruyu örtmez.
 */

export type DrawingSheet = { dataUrl: string; width: number; height: number };

const COLORS = [
  { name: "Siyah", value: "#1a1a2e" },
  { name: "Kırmızı", value: "#e63946" },
  { name: "Mavi", value: "#0077b6" },
  { name: "Yeşil", value: "#2a9d8f" },
  { name: "Turuncu", value: "#f4a261" },
  { name: "Mor", value: "#7209b7" },
];

export default function DrawingBoard({
  onClose,
  className = "",
  toolbarHost,
  sheets,
  sheetKey,
}: {
  onClose: () => void;
  className?: string;
  toolbarHost?: HTMLElement | null;
  sheets?: Map<string, DrawingSheet>;
  sheetKey?: string;
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const activePointerRef = useRef<number | null>(null);
  const [tool, setTool] = useState<"pen" | "eraser">("pen");
  const [penSize, setPenSize] = useState(3);
  const [eraserSize, setEraserSize] = useState(16);
  const [color, setColor] = useState(COLORS[0].value);
  const [opaque, setOpaque] = useState(false);
  const [interactive, setInteractive] = useState(true);

  // Yeniden boyutlandırmada çizim korunur: canvas'ın piksel boyutu değişince içerik
  // silinir, o yüzden önce kopyalanıp sonra geri basılır.
  const resizeCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = (sheets ? canvas.parentElement || canvas : canvas).getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;

    const dpr = window.devicePixelRatio || 1;
    const nextWidth = Math.round(rect.width * dpr);
    const nextHeight = Math.round(rect.height * dpr);
    if (canvas.width === nextWidth && canvas.height === nextHeight) return;

    const previous = canvas.width > 0 && canvas.height > 0 ? document.createElement("canvas") : null;
    if (previous) {
      previous.width = canvas.width;
      previous.height = canvas.height;
      previous.getContext("2d")?.drawImage(canvas, 0, 0);
    }

    canvas.width = nextWidth;
    canvas.height = nextHeight;
    canvas.style.width = `${rect.width}px`;
    canvas.style.height = `${rect.height}px`;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.scale(dpr, dpr);
    if (!previous) return;
    if (sheets) {
      // Soru görseli ortalı: çizim esnetilmez, soruyla aynı yerde kalır
      ctx.drawImage(previous, (rect.width - previous.width / dpr) / 2, 0, previous.width / dpr, previous.height / dpr);
    } else {
      ctx.drawImage(previous, 0, 0, rect.width, rect.height);
    }
  }, [sheets]);

  const wipeCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return;
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.restore();
  }, []);

  useEffect(() => {
    resizeCanvas();
    window.addEventListener("resize", resizeCanvas);
    window.addEventListener("orientationchange", resizeCanvas);
    const parent = canvasRef.current?.parentElement;
    const observer = sheets && parent ? new ResizeObserver(resizeCanvas) : null;
    if (observer && parent) observer.observe(parent);
    return () => {
      window.removeEventListener("resize", resizeCanvas);
      window.removeEventListener("orientationchange", resizeCanvas);
      observer?.disconnect();
    };
  }, [resizeCanvas, sheets]);

  // Soru değişince canvas o sorunun çizimine döner
  useEffect(() => {
    if (!sheets) return undefined;
    let cancelled = false;
    resizeCanvas();
    wipeCanvas();
    const sheet = sheetKey ? sheets.get(sheetKey) : undefined;
    const canvas = canvasRef.current;
    if (!sheet || !canvas) return undefined;
    const image = new Image();
    image.onload = () => {
      if (cancelled) return;
      const width = canvas.width / (window.devicePixelRatio || 1);
      canvas.getContext("2d")?.drawImage(image, (width - sheet.width) / 2, 0, sheet.width, sheet.height);
    };
    image.src = sheet.dataUrl;
    return () => {
      cancelled = true;
    };
  }, [sheetKey, sheets, resizeCanvas, wipeCanvas]);

  const saveSheet = () => {
    const canvas = canvasRef.current;
    if (!canvas || !sheets || !sheetKey) return;
    const dpr = window.devicePixelRatio || 1;
    sheets.set(sheetKey, { dataUrl: canvas.toDataURL("image/png"), width: canvas.width / dpr, height: canvas.height / dpr });
  };

  const pointFromEvent = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  };

  const applyStroke = (ctx: CanvasRenderingContext2D) => {
    if (tool === "pen") {
      ctx.globalCompositeOperation = "source-over";
      ctx.strokeStyle = color;
      ctx.lineWidth = penSize;
    } else {
      ctx.globalCompositeOperation = "destination-out";
      ctx.lineWidth = eraserSize;
    }
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
  };

  const handlePointerDown = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (activePointerRef.current !== null) return;
    activePointerRef.current = event.pointerId;
    event.currentTarget.setPointerCapture(event.pointerId);

    const ctx = event.currentTarget.getContext("2d");
    if (!ctx) return;
    const { x, y } = pointFromEvent(event);
    applyStroke(ctx);
    ctx.beginPath();
    ctx.moveTo(x, y);
    // Tek dokunuş da iz bıraksın (nokta koyma)
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (activePointerRef.current !== event.pointerId) return;
    const ctx = event.currentTarget.getContext("2d");
    if (!ctx) return;
    const { x, y } = pointFromEvent(event);
    applyStroke(ctx);
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const endStroke = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (activePointerRef.current !== event.pointerId) return;
    activePointerRef.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    saveSheet();
  };

  const clearBoard = () => {
    wipeCanvas();
    if (sheetKey) sheets?.delete(sheetKey);
  };

  const activeSize = tool === "pen" ? penSize : eraserSize;
  const toolButtonClass = (active: boolean) =>
    `flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium transition-colors ${
      active ? "bg-purple-600 text-white" : "bg-white text-slate-600 hover:bg-slate-100"
    }`;

  const toolbar = (
      <div className="pointer-events-auto m-2 flex flex-wrap items-center gap-2 rounded-xl border border-slate-200 bg-white/95 px-3 py-2 shadow-lg backdrop-blur">
        <div className="flex overflow-hidden rounded-lg border border-slate-300">
          <button
            type="button"
            onClick={() => { setTool("pen"); setInteractive(true); }}
            className={toolButtonClass(interactive && tool === "pen")}
            aria-pressed={interactive && tool === "pen"}>
            <Pencil className="h-3.5 w-3.5" />
            Kalem
          </button>
          <button
            type="button"
            onClick={() => { setTool("eraser"); setInteractive(true); }}
            className={`border-l border-slate-300 ${toolButtonClass(interactive && tool === "eraser")}`}
            aria-pressed={interactive && tool === "eraser"}>
            <Eraser className="h-3.5 w-3.5" />
            Silgi
          </button>
          <button
            type="button"
            onClick={() => setInteractive(false)}
            title="Çizimi duraklat — PDF'i kaydır, teste cevap ver"
            className={`border-l border-slate-300 ${toolButtonClass(!interactive)}`}
            aria-pressed={!interactive}>
            <Hand className="h-3.5 w-3.5" />
            İçeriğe dokun
          </button>
        </div>

        {interactive && tool === "pen" && (
          <div className="flex items-center gap-1.5">
            {COLORS.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => setColor(option.value)}
                title={option.name}
                aria-label={option.name}
                className={`h-6 w-6 rounded-full border-2 transition-all ${
                  color === option.value ? "border-white ring-2 ring-slate-400 scale-110" : "border-slate-300 hover:scale-105"
                }`}
                style={{ backgroundColor: option.value }}
              />
            ))}
          </div>
        )}

        {interactive && (
          <label className="flex items-center gap-2 text-xs text-slate-600">
            <span className="hidden sm:inline">{tool === "pen" ? "Kalınlık" : "Silgi"}</span>
            <input
              type="range"
              min={tool === "pen" ? 1 : 8}
              max={tool === "pen" ? 12 : 48}
              value={activeSize}
              onChange={(event) => {
                const value = Number(event.target.value);
                if (tool === "pen") setPenSize(value);
                else setEraserSize(value);
              }}
              className="w-20 accent-purple-600"
            />
          </label>
        )}

        <div className="ml-auto flex items-center gap-1">
          <button
            type="button"
            onClick={() => setOpaque((prev) => !prev)}
            title={opaque ? "Zemini şeffaflaştır — materyali gör" : "Beyaz zemin — temiz sayfa"}
            aria-pressed={opaque}
            className={`rounded-lg p-2 transition-colors ${
              opaque ? "bg-purple-600 text-white" : "text-slate-500 hover:bg-slate-200 hover:text-slate-800"
            }`}>
            <Square className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={clearBoard}
            className="rounded-lg bg-red-100 p-2 text-red-600 transition-colors hover:bg-red-200"
            title="Çizimi temizle"
            aria-label="Çizimi temizle">
            <RotateCcw className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-slate-500 transition-colors hover:bg-slate-200 hover:text-slate-800"
            title="Çizimi kapat"
            aria-label="Çizimi kapat">
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
  );

  return (
    // Kök katman tıklamaları GEÇİRİR; yalnız araç çubuğu ve (çizim açıkken) canvas yakalar.
    <div className={`pointer-events-none flex flex-col ${className}`}>
      {toolbarHost === undefined ? toolbar : toolbarHost && createPortal(toolbar, toolbarHost)}

      <div className={`min-h-0 flex-1 ${toolbarHost === undefined ? "px-2 pb-2" : ""}`}>
        <canvas
          ref={canvasRef}
          className={`h-full w-full rounded-lg transition-colors ${
            opaque ? "border border-slate-300 bg-white" : "bg-transparent"
          } ${interactive ? "pointer-events-auto touch-none" : "pointer-events-none"}`}
          style={{ cursor: tool === "eraser" ? "cell" : "crosshair" }}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={endStroke}
          onPointerCancel={endStroke}
          onPointerLeave={endStroke}
        />
      </div>
    </div>
  );
}
